/* Lightweight logger with level/tag gating for web and mobile (Expo/React Native)
 * Usage:
 *   log('PostsList', 'info', 'message', {k:1})
 *   debug('Reactions', 'catalog loaded', meta)
 *   lazy('PostsList', 'debug', () => ['heavy object:', JSON.stringify(obj)])
 *   const throttled = createThrottled('PostsList', 'debug', 500)
 *   throttled('onScroll', payload)
 */

export type LogLevel = 'error' | 'warn' | 'info' | 'debug'
const levelOrder: Record<LogLevel, number> = { error: 0, warn: 1, info: 2, debug: 3 }

function parseLevel(value: any): LogLevel {
  const v = String(value || '').toLowerCase()
  if (v === 'error' || v === 'warn' || v === 'info' || v === 'debug') return v
  return typeof __DEV__ !== 'undefined' && __DEV__ ? 'debug' : 'warn'
}

function getEnabledTags(): Set<string> {
  const raw = (process as any)?.env?.EXPO_PUBLIC_DEBUG_TAGS || (process as any)?.env?.DEBUG_TAGS || ''
  return new Set(String(raw).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))
}
function getDisabledTags(): Set<string> {
  const raw = (process as any)?.env?.EXPO_PUBLIC_DEBUG_DISABLE_TAGS || (process as any)?.env?.DEBUG_DISABLE_TAGS || ''
  return new Set(String(raw).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))
}

function getDefaultLevel(): LogLevel {
  const envLevel = (process as any)?.env?.EXPO_PUBLIC_DEBUG_LEVEL || (process as any)?.env?.DEBUG_LEVEL
  if (envLevel) return parseLevel(envLevel)
  const nodeEnv = (process as any)?.env?.NODE_ENV
  if (nodeEnv === 'production') return 'error'
  return typeof __DEV__ !== 'undefined' && __DEV__ ? 'debug' : 'info'
}

function shouldLog(tag: string, level: LogLevel): boolean {
  try {
    const disabled = getDisabledTags()
    if (disabled.has(String(tag || '').toLowerCase())) return false

    const defaultLevel = getDefaultLevel()
    let allowedLevel = defaultLevel

    // If tag explicitly enabled, allow debug for that tag
    const enabled = getEnabledTags()
    if (enabled.has(String(tag || '').toLowerCase())) allowedLevel = 'debug'

    return levelOrder[level] <= levelOrder[allowedLevel]
  } catch {
    // Fallback: never break app due to logger errors
    return true
  }
}

// Redaction helpers to avoid leaking sensitive data in logs
const SENSITIVE_KEYS = new Set([
  'authorization', 'auth', 'token', 'access_token', 'accesstoken', 'id_token', 'idtoken', 'refresh_token', 'refreshtoken',
  'password', 'pwd', 'pass', 'secret', 'clientsecret', 'client_secret', 'api_key', 'apikey', 'x-api-key', 'x_api_key',
  'session', 'cookie', 'cookies',
  // AWS Cognito identifiers (not critical secrets but better not to spam exact values in logs)
  'userpoolid', 'user_pool_id', 'identitypoolid', 'identity_pool_id', 'userpoolwebclientid', 'user_pool_web_client_id'
])

function maskString(value: string, visibleStart = 3, visibleEnd = 3): string {
  if (!value) return ''
  const len = value.length
  if (len <= visibleStart + visibleEnd) return '*'.repeat(Math.max(4, len))
  const start = value.slice(0, visibleStart)
  const end = value.slice(-visibleEnd)
  return `${start}${'*'.repeat(len - visibleStart - visibleEnd)}${end}`
}

function sanitizeUrl(u: string): string {
  try {
    const url = new URL(u)
    const params = url.searchParams
    const redactParams = ['token', 'access_token', 'id_token', 'api_key', 'apikey', 'key', 'signature']
    for (const k of redactParams) {
      if (params.has(k)) params.set(k, '***')
    }
    return url.toString()
  } catch { return u }
}

function redactValue(val: any, keyHint?: string): any {
  try {
    if (val == null) return val
    if (typeof val === 'string') {
      // If key hints sensitive or string looks like Bearer/eyJ JWT or contains URL with sensitive params
      const lower = (keyHint || '').toLowerCase()
      if (SENSITIVE_KEYS.has(lower) || /^bearer\s+/i.test(val) || val.startsWith('eyJ')) return maskString(val)
      if (/https?:\/\//i.test(val)) return sanitizeUrl(val)
      return val
    }
    if (typeof val !== 'object') return val
    if (val instanceof Error) {
      // Do not mutate Error, just return shallow safe copy
      return { name: val.name, message: val.message, stack: val.stack }
    }
    const seen = new WeakSet()
    const helper = (o: any): any => {
      if (o == null || typeof o !== 'object') return o
      if (seen.has(o)) return '[Circular]'
      seen.add(o)
      if (Array.isArray(o)) return o.map((x) => helper(x))
      const out: any = {}
      for (const k of Object.keys(o)) {
        const v = (o as any)[k]
        const lowerK = k.toLowerCase()
        if (SENSITIVE_KEYS.has(lowerK)) {
          out[k] = typeof v === 'string' ? maskString(v) : '***'
        } else if (typeof v === 'string' && /https?:\/\//i.test(v)) {
          out[k] = sanitizeUrl(v)
        } else {
          out[k] = helper(v)
        }
      }
      return out
    }
    return helper(val)
  } catch { return val }
}

function sanitizeArgs(args: any[]): any[] {
  try { return args.map((a) => redactValue(a)) } catch { return args }
}

export function log(tag: string, level: LogLevel, ...args: any[]) {
  if (!shouldLog(tag, level)) return
  const prefix = `[${tag}]`
  const safeArgs = sanitizeArgs(args)
  if (level === 'error') console.error(prefix, ...safeArgs)
  else if (level === 'warn') console.warn(prefix, ...safeArgs)
  else if (level === 'info') console.log(prefix, ...safeArgs)
  else console.log(prefix, ...safeArgs)
}

export const debug = (tag: string, ...args: any[]) => log(tag, 'debug', ...args)
export const info = (tag: string, ...args: any[]) => log(tag, 'info', ...args)
export const warn = (tag: string, ...args: any[]) => log(tag, 'warn', ...args)
export const error = (tag: string, ...args: any[]) => log(tag, 'error', ...args)

// Lazy logger: compute args only if it will actually log
export function lazy(tag: string, level: LogLevel, producer: () => any[]) {
  if (!shouldLog(tag, level)) return
  try {
    const out = producer()
    const arr = Array.isArray(out) ? out : [out]
    const safeArr = sanitizeArgs(arr)
    log(tag, level, ...safeArr)
  } catch (e) {
    // If producer fails, at least signal it in debug
    if (shouldLog(tag, 'debug')) console.warn(`[${tag}] lazy log producer error:`, (e as any)?.message || e)
  }
}

// Throttled logger helper
export function createThrottled(tag: string, level: LogLevel, intervalMs = 500) {
  let last = 0
  return (message: string, ...args: any[]) => {
    const now = Date.now()
    if (now - last >= intervalMs) {
      last = now
      log(tag, level, message, ...args)
    }
  }
}

// Explicit helper to manually redact objects/strings before logging if needed
export function redact<T = any>(value: T): T {
  return redactValue(value) as T
}
